import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Plus, Minus, ShoppingBag, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { createCheckout, prodImg } from '../lib/products';

const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);

const STORAGE = 'medtrend_cart';

export const CartProvider = ({ children }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState({ name: '', email: '', company: '', country: '' });

  useEffect(() => {
    localStorage.setItem(STORAGE, JSON.stringify(items));
  }, [items]);

  const add = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const found = prev.find((i) => i.slug === product.slug);
      if (found) return prev.map((i) => (i.slug === product.slug ? { ...i, qty: i.qty + qty } : i));
      return [...prev, { slug: product.slug, name: product.name, price: Number(product.price) || 0, img: product.image, qty }];
    });
    toast.success(`${product.name} added to cart`);
    setOpen(true);
  }, []);

  const remove = useCallback((slug) => setItems((p) => p.filter((i) => i.slug !== slug)), []);
  const setQty = useCallback((slug, qty) => {
    if (qty < 1) return remove(slug);
    setItems((p) => p.map((i) => (i.slug === slug ? { ...i, qty } : i)));
  }, [remove]);
  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((sum, i) => sum + i.qty, 0);
  const total = items.reduce((sum, i) => sum + i.price * i.qty, 0);

  const checkout = async () => {
    if (!items.length) return toast.error('Cart is empty.');
    if (!customer.name || !customer.email) return toast.error('Full name and email are required.');

    setLoading(true);
    try {
      const data = await createCheckout({
        ...customer,
        items: items.map((i) => ({ slug: i.slug, qty: i.qty })),
        origin_url: window.location.origin,
      });

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error('Checkout failed to generate URL');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Checkout failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CartCtx.Provider value={{ items, add, remove, setQty, clear, count, total, open, setOpen }}>
      {children}

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm">
            <div className="absolute inset-0" onClick={() => setOpen(false)} />
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              data-testid="cart-drawer"
              className="absolute right-0 top-0 bottom-0 flex w-full max-w-md flex-col bg-white shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-mt-blue/10 text-mt-blue">
                    <ShoppingBag className="h-5 w-5" strokeWidth={2} />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-mt-ink">{t('cart.title')}</h3>
                    <p className="text-xs text-mt-muted">{count} items</p>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4">
                {items.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center text-center text-mt-muted">
                    <ShoppingBag className="h-12 w-12 stroke-[1.25] text-slate-300 mb-3" />
                    <p>{t('cart.empty')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {items.map((i) => (
                      <div key={i.slug} className="flex gap-4 rounded-2xl border border-slate-100 bg-slate-50/50 p-3">
                        <img src={prodImg(i.img)} alt={i.name} className="h-16 w-16 rounded-xl object-contain bg-white p-1" />
                        <div className="flex flex-1 flex-col justify-between">
                          <div className="flex justify-between gap-2">
                            <h4 className="font-display text-sm font-bold text-mt-ink line-clamp-1">{i.name}</h4>
                            <button onClick={() => remove(i.slug)} className="text-slate-400 hover:text-red-500">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <span className="font-display text-sm font-bold text-mt-blue">${i.price}</span>
                            <div className="flex items-center gap-2">
                              <button onClick={() => setQty(i.slug, i.qty - 1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 hover:bg-slate-100">
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="w-5 text-center text-xs font-bold">{i.qty}</span>
                              <button onClick={() => setQty(i.slug, i.qty + 1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 hover:bg-mt-blue hover:text-white">
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {items.length > 0 && (
                <div className="border-t border-slate-100 px-6 py-5">
                  <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <input
                      data-testid="checkout-name"
                      value={customer.name}
                      onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))}
                      placeholder={t('cart.name')}
                      className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-mt-blue focus:outline-none"
                    />
                    <input
                      data-testid="checkout-email"
                      type="email"
                      value={customer.email}
                      onChange={(e) => setCustomer((c) => ({ ...c, email: e.target.value }))}
                      placeholder={t('cart.email')}
                      className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-mt-blue focus:outline-none"
                    />
                    <input
                      data-testid="checkout-company"
                      value={customer.company}
                      onChange={(e) => setCustomer((c) => ({ ...c, company: e.target.value }))}
                      placeholder={t('cart.company')}
                      className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-mt-blue focus:outline-none"
                    />
                    <input
                      data-testid="checkout-country"
                      value={customer.country}
                      onChange={(e) => setCustomer((c) => ({ ...c, country: e.target.value }))}
                      placeholder={t('cart.country')}
                      className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:border-mt-blue focus:outline-none"
                    />
                  </div>
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-mt-muted">{t('cart.subtotal')}</span>
                    <span className="font-display text-xl font-extrabold text-mt-ink" data-testid="cart-total">${total.toFixed(2)}</span>
                  </div>
                  <button
                    data-testid="cart-checkout"
                    onClick={checkout}
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-mt-orange px-8 py-4 font-display font-semibold text-white shadow-[0_10px_34px_rgba(235,93,11,0.25)] transition-all duration-300 hover:bg-mt-orangeLight hover:-translate-y-0.5 disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('cart.checkout')}
                  </button>
                  <p className="mt-3 text-center text-xs text-mt-muted">{t('cart.secure_note')}</p>
                </div>
              )}
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </CartCtx.Provider>
  );
};
